import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { getOrganization, updateOrganization, getOrganizations, uploadOrgLogo, updateProfile } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Settings as SettingsIcon, 
  Building2, 
  Upload,
  Save,
  Loader2,
  Bell,
  User,
  ImageIcon,
  Trash2,
  AlertTriangle,
  Network,
  Lock,
  Mail,
  Eye,
  EyeOff
} from 'lucide-react';
import NotificationPreferences from '../components/NotificationPreferences';
import IncidentConfiguration from '../components/IncidentConfiguration';
import OrganizationHierarchy from '../components/OrganizationHierarchy';
import BrokerAssignment from '../components/BrokerAssignment';

export default function Settings() {
  const { user, isSuperAdmin, login } = useAuth();
  const { selectedOrg, operatingMode } = useAdminMode();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    contact_email: '',
    contact_phone: '',
  });
  
  // Account/Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const isSuper = isSuperAdmin();
  
  // Determine which org to use - selected org takes priority over user's org
  const currentOrgId = selectedOrg?.id || user?.organization_id;

  // Initialize profile data when user loads
  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  const fetchOrganization = async () => {
    if (!currentOrgId) {
      // No org selected - try to get first org as fallback
      try {
        const orgsRes = await getOrganizations();
        if (orgsRes.data.length > 0) {
          setOrganization(orgsRes.data[0]);
          setFormData({
            name: orgsRes.data[0].name || '',
            logo_url: orgsRes.data[0].logo_url || '',
            address: orgsRes.data[0].address || '',
            city: orgsRes.data[0].city || '',
            state: orgsRes.data[0].state || '',
            zip_code: orgsRes.data[0].zip_code || '',
            contact_email: orgsRes.data[0].contact_email || '',
            contact_phone: orgsRes.data[0].contact_phone || '',
          });
        }
      } catch (e) {
        console.error('Failed to fetch organizations');
      }
      setLoading(false);
      return;
    }
    
    try {
      const response = await getOrganization(currentOrgId);
      setOrganization(response.data);
      setFormData({
        name: response.data.name || '',
        logo_url: response.data.logo_url || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
      });
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!organization?.id) return;
    
    setSaving(true);
    try {
      await updateOrganization(organization.id, formData);
      toast.success('Settings saved');
      fetchOrganization();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (PNG, JPG, WebP, or SVG)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const response = await uploadOrgLogo(organization.id, file);
      const logoUrl = response.data.logo_url;
      setFormData(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploaded successfully');
      // Auto-save the organization with new logo
      await updateOrganization(organization.id, { ...formData, logo_url: logoUrl });
      fetchOrganization();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!organization?.id) return;
    
    setSaving(true);
    try {
      await updateOrganization(organization.id, { ...formData, logo_url: '' });
      setFormData(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo removed');
      fetchOrganization();
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setSaving(false);
    }
  };

  // Handle profile/account update
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updateData = {};
      
      // Only include changed fields
      if (profileData.name && profileData.name !== user?.name) {
        updateData.name = profileData.name;
      }
      if (profileData.email && profileData.email !== user?.email) {
        updateData.email = profileData.email;
      }
      
      // Handle password change
      if (profileData.new_password) {
        if (profileData.new_password !== profileData.confirm_password) {
          toast.error('New passwords do not match');
          setSavingProfile(false);
          return;
        }
        if (!profileData.current_password) {
          toast.error('Current password is required to change password');
          setSavingProfile(false);
          return;
        }
        if (profileData.new_password.length < 6) {
          toast.error('New password must be at least 6 characters');
          setSavingProfile(false);
          return;
        }
        updateData.current_password = profileData.current_password;
        updateData.new_password = profileData.new_password;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setSavingProfile(false);
        return;
      }
      
      const response = await updateProfile(updateData);
      
      // Update local auth context with new user data
      const token = localStorage.getItem('token');
      if (token && response.data) {
        login(token, response.data);
      }
      
      // Clear password fields
      setProfileData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      
      toast.success('Profile updated successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(typeof message === 'string' ? message : 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-4 sm:space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
          Settings
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">
          Manage your organization profile and notification preferences
        </p>
      </div>

      {/* Organization Context Banner */}
      {organization && (
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg border border-primary/30 bg-primary/5">
          <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-xs sm:text-sm truncate">
            Managing settings for <strong>{organization.name}</strong>
          </span>
        </div>
      )}

      <Tabs defaultValue="organization" className="w-full">
        <TabsList className={`grid w-full ${isSuper ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-3'} mb-4 sm:mb-6 h-auto`}>
          <TabsTrigger value="organization" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="org-tab">
            <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Organization</span>
            <span className="sm:hidden">Org</span>
          </TabsTrigger>
          {isSuper && (
            <TabsTrigger value="incidents" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="incidents-config-tab">
              <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Incidents</span>
              <span className="sm:hidden">Inc</span>
            </TabsTrigger>
          )}
          {isSuper && (
            <TabsTrigger value="hierarchy" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="hierarchy-tab">
              <Network className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Hierarchy</span>
              <span className="sm:hidden">Hier</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="notifications-tab">
            <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Notifications</span>
            <span className="sm:hidden">Notif</span>
          </TabsTrigger>
          <TabsTrigger value="account" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="account-tab">
            <User className="w-4 h-4" />
            Account
          </TabsTrigger>
        </TabsList>

        {/* Organization Tab - Available to all admins */}
        <TabsContent value="organization">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Organization Profile
              </CardTitle>
              <CardDescription>
                Your logo will appear throughout your risk management experience including the login page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload Section */}
              <div className="p-4 rounded-lg border border-dashed border-border bg-secondary/20">
                <div className="flex items-start gap-6">
                  {/* Logo Preview */}
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-lg bg-background border-2 border-border flex items-center justify-center overflow-hidden">
                      {formData.logo_url ? (
                        <img 
                          src={formData.logo_url} 
                          alt="Organization logo" 
                          className="w-full h-full object-contain p-2"
                          data-testid="org-logo-preview"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="text-center p-2">
                          <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto mb-1" />
                          <span className="text-xs text-muted-foreground">No Logo</span>
                        </div>
                      )}
                    </div>
                    {formData.logo_url && (
                      <button
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove logo"
                        data-testid="remove-logo-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label className="text-base font-semibold">Organization Logo</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload your logo to brand your risk management experience. 
                        Recommended: Square image, at least 256x256px. Max 5MB.
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLogoUpload}
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Logo
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        PNG, JPG, WebP, or SVG
                      </span>
                    </div>
                    
                    {/* Or enter URL */}
                    <div className="pt-2 border-t border-border">
                      <Label className="text-sm text-muted-foreground">Or enter a logo URL</Label>
                      <Input
                        value={formData.logo_url}
                        onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                        placeholder="https://example.com/logo.png"
                        className="bg-secondary/50 mt-1"
                        data-testid="logo-url-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Organization Name */}
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your Organization"
                  className="bg-secondary/50"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label>Street Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Main St"
                  className="bg-secondary/50"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="City"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    placeholder="State"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    placeholder="12345"
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    placeholder="contact@org.com"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="(555) 555-5555"
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary-glow"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* Broker Assignment - Super Admin only */}
          {isSuper && organization?.id && (
            <div className="mt-4">
              <BrokerAssignment organization={organization} onChanged={fetchOrganization} />
            </div>
          )}
        </TabsContent>

        {/* Incident Configuration Tab - Super Admin Only */}
        {isSuper && (
        <TabsContent value="incidents" data-testid="incidents-config-content">
          <IncidentConfiguration organizationId={currentOrgId} />
        </TabsContent>
        )}

        {/* Hierarchy Tab - Super Admin Only */}
        {isSuper && (
        <TabsContent value="hierarchy" data-testid="hierarchy-content">
          <OrganizationHierarchy />
        </TabsContent>
        )}

        {/* Notifications Tab */}
        <TabsContent value="notifications" data-testid="notifications-content">
          <NotificationPreferences />
        </TabsContent>

        {/* Account Tab */}
        <TabsContent value="account">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Account Settings
              </CardTitle>
              <CardDescription>
                Update your personal information and password
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Profile Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Name</Label>
                  <Input
                    id="profile-name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    placeholder="Your name"
                    className="bg-secondary/50"
                    data-testid="profile-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile-email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="profile-email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    placeholder="your@email.com"
                    className="bg-secondary/50"
                    data-testid="profile-email-input"
                  />
                </div>
                
                <div className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">Role</span>
                  <span className="font-medium capitalize">{user?.role?.replace('_', ' ')}</span>
                </div>
              </div>
              
              {/* Password Change Section */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Change Password
                </h3>
                <p className="text-sm text-muted-foreground">Leave blank if you don't want to change your password</p>
                
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={profileData.current_password}
                      onChange={(e) => setProfileData({...profileData, current_password: e.target.value})}
                      placeholder="Enter current password"
                      className="bg-secondary/50 pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        value={profileData.new_password}
                        onChange={(e) => setProfileData({...profileData, new_password: e.target.value})}
                        placeholder="Enter new password"
                        className="bg-secondary/50 pr-10"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type={showNewPassword ? "text" : "password"}
                      value={profileData.confirm_password}
                      onChange={(e) => setProfileData({...profileData, confirm_password: e.target.value})}
                      placeholder="Confirm new password"
                      className="bg-secondary/50"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                
                {profileData.new_password && profileData.confirm_password && 
                 profileData.new_password !== profileData.confirm_password && (
                  <p className="text-sm text-destructive">Passwords do not match</p>
                )}
              </div>
              
              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="btn-primary-glow"
                data-testid="save-profile-btn"
              >
                {savingProfile ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Profile
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
